import { useMutation } from '@tanstack/react-query'

import { useNames } from '@hooks/names'
import { useFindProfile } from '@hooks/profile'
import { api } from '@services/apiClient'
import { queryClient } from '@services/queryClient'

import { useFindBarriersQueries } from './queries'
import { IBarrier } from '../../interfaces/task/barrier'

interface IBarrierPayload {
  comments: { comment: string }[]
  barrierCodeId: number
  hoursLost: number
  wasCausedByPreviousShift: boolean
}

interface ICreateBarrierParams {
  barrier: IBarrierPayload
  taskId: number
}

export function useCreateBarrier() {
  const { data: user } = useFindProfile()
  const { divideName } = useNames()
  const { firstName, lastName } = divideName(user?.displayName ?? '')

  const { getBarrierCodesQuery, getBarriersQuery } = useFindBarriersQueries()

  const siteId = user && user.selectedSite.id

  const postBarrier = async ({ barrier, taskId }: ICreateBarrierParams) => {
    const { data } = await api.post<IBarrier>(
      `sites/${siteId}/tasks/${taskId}/barriers`,
      barrier,
    )
    return data
  }

  return useMutation({
    mutationFn: postBarrier,
    onMutate: handleOptimisticCreateBarrier,
    onError: (_err, _newBarrier, context) => {
      if (!context) return

      const { previousBarrier, barrierKey } = context
      queryClient.setQueryData(barrierKey, previousBarrier)
    },
    onSettled: (_barriers, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.barrierKey,
      }),
  })

  async function handleOptimisticCreateBarrier({
    taskId,
    barrier,
  }: ICreateBarrierParams) {
    const [barrierKey, barrierResult] = await getBarriersQuery(taskId)
    const barrierCode = await getBarrierCodesQuery(barrier.barrierCodeId)

    if (!barrierKey || !barrierResult) return

    const previousBarrier = barrierResult

    const comments = barrier.comments.map(({ comment }) => {
      return {
        comment,
        createdOn: new Date(),
        id: -1,
        user: { firstName, lastName },
      }
    })

    const newBarrier = {
      ...barrier,
      barrierCode: {
        description: barrierCode?.description ?? 'Not Available',
        isActive: barrierCode?.isActive ?? false,
        id: barrierCode?.id ?? -1,
      },
      taskId,
      barrierCodeId: barrierCode?.id ?? -1,
      user: {
        firstName,
        lastName,
      },
      id: -1,
      comments,
    }

    const updatedBarriers = [...barrierResult, newBarrier]

    queryClient.setQueryData(barrierKey, () => updatedBarriers)

    return { barrierKey, barrierResult, previousBarrier }
  }
}
